import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";

import { getGetStoreInstagramAccountQueryKey } from "@/api";
import type {
  InstagramCallbackFailureReason,
  InstagramCallbackStatus,
} from "@/lib/instagram/constants";
import { useSessionStore } from "@/stores/session.store";

export type InstagramCallbackParams = {
  status?: InstagramCallbackStatus;
  reason?: InstagramCallbackFailureReason;
  storeId?: number;
  username?: string;
};

// The backend finishes the OAuth exchange, then redirects here with the outcome.
export function useInstagramCallbackPage({
  status,
  reason,
  storeId,
  username,
}: InstagramCallbackParams) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setActiveStoreId = useSessionStore((state) => state.setActiveStoreId);
  const isSuccess = status === "success";

  useEffect(() => {
    if (!isSuccess || storeId === undefined) return;
    setActiveStoreId(storeId);
    void queryClient.invalidateQueries({
      queryKey: getGetStoreInstagramAccountQueryKey(storeId),
    });
  }, [isSuccess, queryClient, setActiveStoreId, storeId]);

  return {
    isLoading: status === undefined,
    isSuccess,
    isDenied: status === "denied",
    isFailed: status === "failed",
    username,
    failureReasonKey: `callback.reasons.${reason ?? "unknown"}`,
    onManage: () => void navigate({ to: "/settings", search: { tab: "instagram" } }),
    onGoToDashboard: () => void navigate({ to: "/" }),
  };
}

export type InstagramCallbackView = ReturnType<typeof useInstagramCallbackPage>;
